import { useCallback, useEffect } from "react";

import { Alert, Box, Button, Container, Divider, Stack, Typography } from "@mui/material";

import { allocationTxt } from "../../../assets/texts/CloudAnalytics/allocation";
import { EditableResourceHeader } from "../../../Components/EditableResourceHeader/EditableResourceHeader";
import LoadingButton from "../../../Components/LoadingButton";
import SaveDialog from "../../../Components/SaveComponents/SaveDialog";
import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";
import { AllocationDragAndDrop } from "./AllocationDragGroup/AllocationDragGroup";
import useAllocationDndStore from "./hooks/useAllocationDnd";
import { formStoreManager } from "./hooks/useAllocationFormState";
import { useAllocationSave } from "./hooks/useAllocationSave";
import { useAllocationSaveDialog } from "./hooks/useAllocationSaveDialog";
import useAllocationSaveValidation from "./hooks/useAllocationSaveValidation";
import { allocationBaseProps } from "./utils";

type Props = { edit: true; allocationId: string } | { edit?: false; allocationId?: undefined };

export const Allocation = ({ edit, allocationId }: Props) => {
  const { isError, isSaveAs, isDuplicate, isDirty } = useAllocationSaveValidation({ isEdit: edit });
  const { trackEvent } = useSegmentTrackEvent({ allowNullUser: false });

  const { items, hasUnallocated, setHasUnallocated, allocationName, clear, isAllocationEditor, setAllocationName } =
    useAllocationDndStore();
  const { saving, handleSave, handleClose, getWarnings } = useAllocationSave({
    edit,
    allocationId,
    isDuplicate,
    isSaveAs,
  });

  const {
    openSaveDialog,
    handleSaveDialog,
    handleSaveAsDialog,
    handleDialogSave,
    handleDialogClose,
    saveLabel,
    modalText,
  } = useAllocationSaveDialog({
    isSaveAs,
    isDuplicate,
    getWarnings,
    handleSave,
    items,
    hasUnallocated,
    isEdit: edit,
  });

  // Force hasUnallocated to true when multiple items exist
  useEffect(() => {
    if (items.main.length > 1) {
      setHasUnallocated(true);
    }
  }, [items.main.length, setHasUnallocated]);

  // Clear all dnd store on unmount
  useEffect(
    () => () => {
      clear();
      formStoreManager.clear();
    },
    [clear]
  );

  const handleSaveAllocationName = useCallback(
    async ({ name }: { name: string }) => {
      setAllocationName(name);
      if (edit) {
        handleSave(name, false);
      }
    },
    [edit, handleSave, setAllocationName]
  );

  return (
    <Box sx={{ position: "relative" }}>
      <Container maxWidth="md" sx={{ pt: 4.5 }}>
        <Stack justifyContent="flex-start" alignItems="flex-start" rowGap={1}>
          <Stack direction="row" alignItems="center">
            <Box sx={{ ml: { xs: "-5px", md: "-40px" } }}>
              <EditableResourceHeader
                name={items.main.length > 1 || hasUnallocated ? allocationName : allocationTxt.ALLOCATION_RULE_LABEL}
                handleBack={handleClose}
                disabled={(items.main.length === 1 && !hasUnallocated) || !isAllocationEditor}
                hideDescription
                maxNameLength={50}
                onSave={handleSaveAllocationName}
                resourceName="allocation"
                {...(!edit && { customActionLabel: allocationTxt.ALLOCATION_NAME_CONFIRM })}
              />
            </Box>
          </Stack>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {allocationTxt.ALLOCATION_DESCRIPTION}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ width: "100%", display: { xs: "flex", md: "none" } }}>
            {allocationTxt.ALLOCATION_MOBILE_INFO}
          </Alert>
          <AllocationDragAndDrop disabled={saving || !isAllocationEditor} />
        </Stack>
      </Container>

      {/* Footer with save buttons */}
      <Box
        sx={{
          position: "fixed",
          display: { xs: "none", md: "block" },
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 980,
          backgroundColor: "background.paper",
        }}
      >
        <Divider />
        <Stack direction="row" alignContent="center" justifyContent="center" spacing={2} p={1}>
          <Container maxWidth="md" sx={{ justifyContent: "flex-end", display: "flex" }}>
            <Button color="primary" onClick={handleClose} disabled={saving} variant="text" size="large" sx={{ mr: 3 }}>
              {allocationTxt.ALLOCATION_CANCEL}
            </Button>
            <LoadingButton
              color="primary"
              loading={saving}
              onClick={isSaveAs ? handleSaveAsDialog : handleSaveDialog}
              variant="contained"
              mixpanelEventId="allocation.save"
              segmentTrackEvent={trackEvent}
              segmentEventName="Allocation Save Initiated"
              eventProperties={{
                ...allocationBaseProps,
                allocationId,
              }}
              data-cy="footer-save-button"
              size="large"
              disabled={isError || !isAllocationEditor || !isDirty}
            >
              {saveLabel}
            </LoadingButton>
          </Container>
        </Stack>
      </Box>

      {/* Save dialog */}
      <SaveDialog
        {...modalText}
        open={openSaveDialog}
        onClose={handleDialogClose}
        value={allocationName}
        onSave={handleDialogSave}
        textFieldProps={{ label: allocationTxt.ALLOCATION_NAME_LABEL }}
      />
    </Box>
  );
};
